class MeasurementFinder extends HTMLElement {
    constructor() {
        super();
        this.state = {
            history: [],
            currentStepId: null,
            inputs: {},
            selectedOptionLabel: null,
            selectedOptionType: null,
            optionConfig: null,
            selectedShopUrl: null
        };

        this.steps = this.querySelectorAll('[data-step-id]');

        // Option type configuration
        this.OPTION_TYPE_CONFIG = {
            'inside_mount': {
                roundingRule: 'down_1_8',
                autoFillFields: [
                    { name: 'Mount Type', value: 'inside', type: 'radio' }
                ]
            },
            'outside_mount': {
                roundingRule: 'down_1_8',
                autoFillFields: [
                    { name: 'Mount Type', value: 'outside', type: 'radio' }
                ]
            }
        };

        this.init();
    }

    init() {
        // Initialize with the visible step (active class)
        const activeStep = this.querySelector('.measurement-finder__step-container.active');
        if (activeStep) {
            this.state.currentStepId = activeStep.dataset.stepId;
        } else {
            // Fallback: find first step and show it
            const firstStep = this.querySelector('[data-step-id]');
            if (firstStep) {
                this.transitionTo(firstStep.dataset.stepId);
            }
        }

        this.bindEvents();
    }

    bindEvents() {
        // Option selection
        this.querySelectorAll('[data-action="select-option"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                // Use dataset.nextStepId (from Liquid) or dataset.nextStep (legacy blocks)
                const nextStepId = btn.dataset.nextStepId || btn.dataset.nextStep;
                const optionLabel = btn.dataset.optionLabel;
                const optionType = btn.dataset.optionType || '';
                const optionIndex = btn.dataset.optionIndex;

                // Store selected option info
                if (optionLabel) {
                    this.state.selectedOptionLabel = optionLabel;
                    this.state.selectedOptionType = optionType;

                    // Get configuration for this option type
                    this.state.optionConfig = this.OPTION_TYPE_CONFIG[optionType] || {
                        roundingRule: 'up_1_8',
                        fillDetailSKU: false
                    };
                }

                // If this is the root option step (step_0), capture the corresponding shop now URL
                if (this.state.currentStepId === 'step_0' && optionIndex !== undefined) {
                    const urlIndex = parseInt(optionIndex) + 1;
                    const attrName = `data-shop-url-${urlIndex}`;
                    this.state.selectedShopUrl = this.getAttribute(attrName) || null;
                }

                if (nextStepId) {
                    this.transitionTo(nextStepId);
                }
            });
        });

        // Previous button
        this.querySelectorAll('[data-action="prev-step"]').forEach(btn => {
            btn.addEventListener('click', () => this.goBack());
        });

        // Input validation (Real-time)
        this.querySelectorAll('.measurement-finder__input').forEach(input => {
            input.addEventListener('input', (e) => {
                const stepId = input.closest('[data-step-id]').dataset.stepId;
                this.checkStepValidity(stepId);
            });
        });

        // Continue button (Input step)
        this.querySelectorAll('[data-action="submit-inputs"]').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const stepId = btn.closest('[data-step-id]').dataset.stepId;
                if (this.checkStepValidity(stepId)) {
                    const nextStepId = btn.dataset.nextStepId || btn.dataset.nextStep;
                    if (nextStepId) {
                        this.transitionTo(nextStepId);
                    }
                }
            });
        });

        // Recalculate
        this.querySelectorAll('[data-action="recalculate"]').forEach(btn => {
            btn.addEventListener('click', () => {
                // Reset state
                this.state.history = [];
                this.state.inputs = {};
                this.state.selectedOptionLabel = null;
                this.state.selectedOptionType = null;
                this.state.optionConfig = null;
                this.state.selectedShopUrl = null; // Also reset the URL

                // Hide current
                if (this.state.currentStepId) {
                    const current = this.querySelector(`[data-step-id="${this.state.currentStepId}"]`);
                    if (current) current.classList.remove('active');
                }

                // Show first step
                // We need to find the root step. 
                // In Liquid recursion, IDs are like step_0, step_0_opt_0_next...
                // The root steps are step_0, step_1 (if multiple roots, but usually one flow).
                // We can just reload the page or find the step with empty history?
                // Better: just find the step that was initially active? 
                // Or just find 'step_0'

                const firstStep = this.querySelector('[data-step-id="step_0"]') || this.querySelector('[data-step-id]');
                if (firstStep) {
                    this.transitionTo(firstStep.dataset.stepId);
                }
            });
        });

        // Shop Now - 触发自定义事件用于保存数据
        this.querySelectorAll('a[href].measurement-finder__btn--primary').forEach(link => {
            link.addEventListener('click', (e) => {
                // Only intercept if this is on a result step
                const resultStep = link.closest('[data-step-type="result_step"]');
                if (!resultStep) return;

                const url = link.href; // 使用已经设置好的 href
                const roundingRule = resultStep.dataset.roundRule || 'none';
                const optionLabel = this.state.selectedOptionLabel || '';
                const optionType = this.state.selectedOptionType || '';
                const optionConfig = this.state.optionConfig || { roundingRule: 'up_1_8', fillDetailSKU: false };

                // Apply rounding rule to get display values
                const displayWidth = this.applyRoundingRule(this.lastCalculatedWidth || 0, roundingRule);
                const displayHeight = this.applyRoundingRule(this.lastCalculatedHeight || 0, roundingRule);

                // For 'none' mode, apply smart rounding for storage based on option type
                let storageRoundingRule = roundingRule;
                if (roundingRule === 'none') {
                    storageRoundingRule = optionConfig.roundingRule;
                }

                // Apply storage rounding and extract whole/fraction
                const storageWidth = this.applyRoundingRule(this.lastCalculatedWidth || 0, storageRoundingRule);
                const storageHeight = this.applyRoundingRule(this.lastCalculatedHeight || 0, storageRoundingRule);

                const widthParts = this.extractWholeAndFraction(storageWidth);
                const heightParts = this.extractWholeAndFraction(storageHeight);

                // Dispatch custom event with calculation data
                const shopNowEvent = new CustomEvent('measurement-finder:shop-now', {
                    detail: {
                        width: storageWidth,
                        height: storageHeight,
                        widthWhole: widthParts.whole,
                        widthFraction: widthParts.fraction,
                        heightWhole: heightParts.whole,
                        heightFraction: heightParts.fraction,
                        widthFormatted: this.formatFraction(displayWidth, roundingRule),
                        heightFormatted: this.formatFraction(displayHeight, roundingRule),
                        optionLabel: optionLabel,
                        optionType: optionType,
                        autoFillFields: optionConfig.autoFillFields || [],
                        url: url,
                        inputs: { ...this.state.inputs }
                    },
                    bubbles: true
                });

                // Dispatch event - user can listen to this event and add custom logic
                this.dispatchEvent(shopNowEvent);

                // If event was prevented by external listener, stop navigation
                if (shopNowEvent.defaultPrevented) {
                    e.preventDefault();
                }
                // 否则让浏览器自然跳转到已经设置好的 href
            });
        });
    }

    applyRoundingRule(value, roundRule) {
        // Apply rounding rule and return the rounded value
        let rounded = value;

        if (roundRule === 'none') {
            return this.roundToPrecision(value, 3);
        } else if (roundRule === 'floor') {
            return Math.floor(value);
        } else if (roundRule === 'ceiling') {
            return Math.ceil(value);
        } else {
            // Handle fraction-based rounding (nearest_X_Y, down_X_Y, up_X_Y)
            const match = roundRule.match(/(nearest|down|up)_(\d+)_(\d+)/);
            if (match) {
                const direction = match[1];
                const numerator = parseInt(match[2]);
                const denominator = parseInt(match[3]);

                // Use integer arithmetic to avoid floating point precision issues
                // Convert to minimal unit (e.g., 1/8 for all fractions)
                const valueIn8ths = value * 8;
                const incrementIn8ths = (numerator * 8) / denominator;

                let roundedIn8ths;
                if (direction === 'nearest') {
                    roundedIn8ths = Math.round(valueIn8ths / incrementIn8ths) * incrementIn8ths;
                } else if (direction === 'down') {
                    roundedIn8ths = Math.floor(valueIn8ths / incrementIn8ths) * incrementIn8ths;
                } else if (direction === 'up') {
                    roundedIn8ths = Math.ceil(valueIn8ths / incrementIn8ths) * incrementIn8ths;
                }

                rounded = roundedIn8ths / 8;
            }
        }

        return this.roundToPrecision(rounded, 3);
    }

    extractWholeAndFraction(value) {
        // Extract whole number and fractional part as a fraction string
        const whole = Math.floor(value);
        const decimal = value - whole;

        // Use epsilon to check if effectively zero
        const epsilon = 0.0001;
        if (Math.abs(decimal) < epsilon) {
            return { whole: whole, fraction: '0/0' };
        }

        // Convert to 1/8 fraction
        const precision = 8;
        const numerator = Math.round(decimal * precision);

        // Reduce fraction
        const gcd = (a, b) => b ? gcd(b, a % b) : a;
        const divisor = gcd(numerator, precision);

        const num = numerator / divisor;
        const den = precision / divisor;

        return { whole: whole, fraction: `${num}/${den}` };
    }

    transitionTo(stepId) {
        if (!stepId) return;

        // Hide current
        if (this.state.currentStepId) {
            const current = this.querySelector(`[data-step-id="${this.state.currentStepId}"]`);
            if (current) current.classList.remove('active');
            this.state.history.push(this.state.currentStepId);
        }

        // Show next
        const next = this.querySelector(`[data-step-id="${stepId}"]`);
        if (next) {
            next.classList.add('active');
            this.state.currentStepId = stepId;

            // Update Step Label & Previous Button Visibility
            const stepPrefixSpan = next.querySelector('.measurement-finder__step-prefix');
            // const prevBtn = next.querySelector('[data-action="prev-step"]'); // Handled by Liquid/CSS now

            const isFirstStep = this.state.history.length === 0;

            if (stepPrefixSpan) {
                if (isFirstStep) {
                    stepPrefixSpan.textContent = ''; // Hide label for first step
                } else {
                    const stepNumber = this.state.history.length + 1;
                    const prefix = this.dataset.stepPrefix || 'Step';
                    stepPrefixSpan.textContent = `${prefix} ${stepNumber}`;
                }
            }

            // If it's a result step, calculate and set up the shop now button
            if (next.dataset.stepType === 'result_step') {
                this.calculateResult(next);

                // 设置 SHOP NOW 按钮的 href
                const shopNowBtn = next.querySelector('a.measurement-finder__btn--primary[href]');
                if (shopNowBtn && this.state.selectedShopUrl) {
                    shopNowBtn.href = this.state.selectedShopUrl;
                }
            }
        }
    }

    goBack() {
        if (this.state.history.length === 0) return;
        const prevStepId = this.state.history.pop();

        // Hide current
        const current = this.querySelector(`[data-step-id="${this.state.currentStepId}"]`);
        if (current) current.classList.remove('active');

        // Show prev
        const prev = this.querySelector(`[data-step-id="${prevStepId}"]`);
        if (prev) {
            prev.classList.add('active');
            this.state.currentStepId = prevStepId;

            // Update Step Label
            const stepPrefixSpan = prev.querySelector('.measurement-finder__step-prefix');
            const isFirstStep = this.state.history.length === 0;

            if (stepPrefixSpan) {
                if (isFirstStep) {
                    stepPrefixSpan.textContent = ''; // Hide label for first step
                } else {
                    const stepNumber = this.state.history.length + 1;
                    const prefix = this.dataset.stepPrefix || 'Step';
                    stepPrefixSpan.textContent = `${prefix} ${stepNumber}`;
                }
            }
        }
    }

    checkStepValidity(stepId) {
        const step = this.querySelector(`[data-step-id="${stepId}"]`);
        const inputs = step.querySelectorAll('.measurement-finder__input');
        const continueBtn = step.querySelector('[data-action="submit-inputs"]');
        let valid = true;

        inputs.forEach(input => {
            const val = parseFloat(input.value);
            if (isNaN(val) || val <= 0) {
                valid = false;
            } else {
                this.state.inputs[input.name] = val;
            }
        });

        if (continueBtn) {
            continueBtn.style.display = valid ? 'inline-block' : 'none';
        }

        return valid;
    }

    calculateResult(stepElement) {
        const formulaW = stepElement.dataset.formulaWidth;
        const formulaH = stepElement.dataset.formulaHeight;
        const roundRule = stepElement.dataset.roundRule;

        const valW = this.evaluateFormula(formulaW);
        const valH = this.evaluateFormula(formulaH);

        // Fix floating point precision before storing (round to 1/1000 inch precision)
        this.lastCalculatedWidth = this.roundToPrecision(valW, 3);
        this.lastCalculatedHeight = this.roundToPrecision(valH, 3);

        const resultBox = stepElement.querySelector('.measurement-finder__result-value');
        if (resultBox) {
            const wStr = this.formatFraction(this.lastCalculatedWidth, roundRule);
            const hStr = this.formatFraction(this.lastCalculatedHeight, roundRule);

            // Use innerHTML to render sup/sub tags
            resultBox.innerHTML = `${wStr}"W &times; ${hStr}"L`;
        }
    }

    roundToPrecision(value, decimals) {
        // Round to specified decimal places to eliminate floating point errors
        // e.g., 53.62499999 -> 53.625
        const factor = Math.pow(10, decimals);
        return Math.round(value * factor) / factor;
    }

    evaluateFormula(formula) {
        if (!formula) return 0;

        // Replace variables with values, sorted by length descending to avoid partial matches
        let expression = formula;
        const keys = Object.keys(this.state.inputs).sort((a, b) => b.length - a.length);

        for (const key of keys) {
            expression = expression.replace(new RegExp(key, 'g'), this.state.inputs[key]);
        }
        // Handle MIN/MAX
        expression = expression.replace(/MIN/g, 'Math.min').replace(/MAX/g, 'Math.max');

        // Safe eval
        return new Function('return ' + expression)();
    }

    formatFraction(value, roundRule) {
        if (isNaN(value)) return "0";

        let rounded = value;

        // Apply rounding rules
        if (roundRule === 'none') {
            // Show decimal as-is
            return this.roundToPrecision(value, 3).toString();
        } else if (roundRule === 'floor') {
            // Round down to whole number
            return Math.floor(value).toString();
        } else if (roundRule === 'ceiling') {
            // Round up to whole number
            return Math.ceil(value).toString();
        } else {
            // Handle fraction-based rounding (nearest_X_Y, down_X_Y, up_X_Y)
            const match = roundRule.match(/(nearest|down|up)_(\d+)_(\d+)/);
            if (match) {
                const direction = match[1];  // 'nearest', 'down', or 'up'
                const numerator = parseInt(match[2]);
                const denominator = parseInt(match[3]);

                // Use integer arithmetic to avoid floating point precision issues
                // Convert to minimal unit (e.g., 1/8 for all fractions)
                const valueIn8ths = value * 8;
                const incrementIn8ths = (numerator * 8) / denominator;

                let roundedIn8ths;
                if (direction === 'nearest') {
                    roundedIn8ths = Math.round(valueIn8ths / incrementIn8ths) * incrementIn8ths;
                } else if (direction === 'down') {
                    roundedIn8ths = Math.floor(valueIn8ths / incrementIn8ths) * incrementIn8ths;
                } else if (direction === 'up') {
                    roundedIn8ths = Math.ceil(valueIn8ths / incrementIn8ths) * incrementIn8ths;
                }

                // Convert back to decimal
                rounded = roundedIn8ths / 8;
            }
        }

        // Round to avoid precision issues (e.g., 5.624999 -> 5.625)
        rounded = this.roundToPrecision(rounded, 3);

        const whole = Math.floor(rounded);
        const fraction = rounded - whole;


        // Use epsilon comparison to check if fraction is effectively zero
        const epsilon = 0.0001;
        if (Math.abs(fraction) < epsilon) return `${whole}`;

        // Convert decimal to fraction (1/8 precision)
        const precision = 8;
        const numerator = Math.round(fraction * precision);

        // Reduce fraction
        const gcd = (a, b) => b ? gcd(b, a % b) : a;
        const divisor = gcd(numerator, precision);

        const num = numerator / divisor;
        const den = precision / divisor;

        // Return HTML string
        return `${whole}&nbsp;<sup class="fraction-sup">${num}</sup>&frasl;<sub class="fraction-sub">${den}</sub>`;
    }
}

customElements.define('measurement-finder', MeasurementFinder);
